import React, { useState } from 'react';
import type { Column } from '@tanstack/react-table';
import { CalendarIcon } from 'lucide-react';
import { parse, isValid } from 'date-fns';
import { toDateString } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/global/base/popover.tsx';
import { Calendar } from '@/global/base/calendar.tsx';
import { Button } from '@/global/base/button.tsx';

type DateColumnFilterProps<TData> = {
    column: Column<TData, unknown>;
};

type CalendarState = {
    isOpen: boolean;
    month: Date;
    isInvalid: boolean;
};

const DateColumnFilter = <TData,>({ column }: DateColumnFilterProps<TData>) => {
    const [calendarState, setCalendarState] = useState<CalendarState>({
        isOpen: false,
        month: new Date(),
        isInvalid: false
    });

    const filterValue = column.getFilterValue() as string;

    const handleDateInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;

        if (inputValue) {
            const parsedDate = parse(inputValue, 'yyyy-MM-dd', new Date());
            if (isValid(parsedDate)) {
                column.setFilterValue(toDateString(parsedDate));
                setCalendarState((previous) => ({ ...previous, month: parsedDate, isInvalid: false }));
            } else {
                column.setFilterValue('');
                setCalendarState((previous) => ({ ...previous, isInvalid: true }));
            }
        } else {
            column.setFilterValue('');
            setCalendarState((previous) => ({ ...previous, isInvalid: false }));
        }
    };

    const handleDateBlur = () => {
        if (!filterValue) {
            setCalendarState((previous) => ({ ...previous, isInvalid: false }));

            return;
        }

        try {
            // Since we're using toDateString consistently, filterValue should always be 'yyyy-MM-dd'
            const parsedDate = parse(filterValue, 'yyyy-MM-dd', new Date());

            if (isValid(parsedDate)) {
                setCalendarState((previous) => ({ ...previous, month: parsedDate, isInvalid: false }));
            } else {
                setCalendarState((previous) => ({ ...previous, isInvalid: true }));
            }
        } catch {
            setCalendarState((previous) => ({ ...previous, isInvalid: true }));
        }
    };

    const formatDateForInput = (dateString: string | undefined) => {
        if (!dateString) return '';

        return dateString; // Already in 'yyyy-MM-dd' format from toDateString()
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center space-x-2">
                <input
                    type="date"
                    className={`h-8 flex-grow border rounded px-2 py-1 text-sm w-full ${
                        calendarState.isInvalid ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={formatDateForInput(filterValue)}
                    onChange={handleDateInputChange}
                    onBlur={handleDateBlur}
                    placeholder="Select date..."
                />

                <Popover
                    open={calendarState.isOpen}
                    onOpenChange={(open) => setCalendarState((previous) => ({ ...previous, isOpen: open }))}
                >
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => setCalendarState((previous) => ({ ...previous, isOpen: !previous.isOpen }))}
                        >
                            <CalendarIcon className="h-4 w-4" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                        <Calendar
                            mode="single"
                            selected={filterValue ? parse(filterValue, 'yyyy-MM-dd', new Date()) : undefined}
                            onSelect={(date) => {
                                column.setFilterValue(date ? toDateString(date) : '');
                                setCalendarState((previous) => ({
                                    ...previous,
                                    isOpen: false,
                                    isInvalid: false,
                                    month: date || new Date()
                                }));
                            }}
                            month={calendarState.month}
                            onMonthChange={(month) => setCalendarState((previous) => ({ ...previous, month }))}
                            disabled={(date) => date < new Date('1900-01-01')}
                        />
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
};

export default DateColumnFilter;
